import React, { useState } from "react";
import {
    Box,
    SxProps,
    Theme,
    Dialog,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    DialogTitle,
    DialogContent,
} from "@mui/material";
import { Typography } from "./wrappers/Typography";
import { useTranslation } from "react-i18next";
import { Button } from "./wrappers/Button";
import PrintHeader, { PrintFilter } from "./PrintHeader";

interface PageTitleProps {
    title?: string;
    titleTx?: string;
    subtitle?: string;
    subtitleTx?: string;
    onExport?: (filetype?: string) => void;
    disableExport?: boolean;
    sx?: SxProps<Theme>;
    printFilters?: PrintFilter[];
    printTitle?: string;
    printTitleTx?: string;
    printSubtitle?: string;
    printSubtitleTx?: string;
    /** Suppress the print-only header (e.g. when the parent renders its own). */
    hidePrintHeader?: boolean;
}

const PageTitle: React.FC<PageTitleProps> = ({
    title,
    titleTx,
    subtitle,
    subtitleTx,
    onExport,
    disableExport,
    sx,
    printFilters,
    printTitle,
    printTitleTx,
    printSubtitle,
    printSubtitleTx,
    hidePrintHeader,
}) => {
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [filetype, setFiletype] = useState("PDF");

    const handleExport = () => {
        if (filetype === "PDF") {
            setShowExportDialog(false);
            setTimeout(() => {
                window.print();
            }, 500);
        } else if (onExport) {
            onExport(filetype);
            setShowExportDialog(false);
        }
    };

    const { t } = useTranslation();

    return (
        <>
            {!hidePrintHeader && (
                <PrintHeader
                    title={printTitle ?? title}
                    titleTx={printTitleTx ?? titleTx}
                    subtitle={printSubtitle ?? subtitle}
                    subtitleTx={printSubtitleTx ?? subtitleTx}
                    filters={printFilters}
                />
            )}
            <Box
                className="screen-only"
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 3,
                    flexWrap: "wrap",
                    gap: 2,
                    ...sx,
                }}
            >
                <Box>
                    {(title || titleTx) && (
                        <Typography tx={titleTx} variant="h6" component="h2">
                            {title}
                        </Typography>
                    )}
                    {(subtitle || subtitleTx) && (
                        <Typography
                            tx={subtitleTx}
                            className="screen-only"
                            variant="body2"
                            color="text.primary"
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {onExport && (
                    <Button
                        disabled={disableExport}
                        tx="ui.dialogs.export.title"
                        variant="contained"
                        onClick={() => setShowExportDialog(true)}
                    />
                )}
            </Box>
            <Dialog open={showExportDialog} maxWidth="lg">
                <DialogTitle variant="h7" fontWeight={500} sx={{ paddingBottom: 0 }}>
                    {t("ui.dialogs.export.title")}
                </DialogTitle>
                <DialogContent sx={{ width: 442 }}>
                    <FormControl fullWidth margin="normal" size="small">
                        <InputLabel id="filetype-select-label">{t("ui.dialogs.export.fileFormatLabel")}</InputLabel>
                        <Select
                            labelId="filetype-select-label"
                            id="filetype-select"
                            value={filetype}
                            label={t("ui.dialogs.export.fileFormatLabel")}
                            onChange={(e) => setFiletype(e.target.value)}
                        >
                            <MenuItem value={"PDF"}>{t("ui.dialogs.export.formats.pdf")}</MenuItem>
                            <MenuItem value={"Excel"}>{t("ui.dialogs.export.formats.excel")}</MenuItem>
                            <MenuItem value={"CSV"}>{t("ui.dialogs.export.formats.csv")}</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 1, pb: 1 }}>
                    <Button
                        sx={{ textTransform: "none" }}
                        onClick={() => setShowExportDialog(false)}
                        variant="text"
                        tx="ui.buttons.cancel"
                    />
                    <Button tx="ui.buttons.export" onClick={handleExport} variant="contained" />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PageTitle;
